import { RouterProvider } from "react-router-dom";
import { router } from "@/router";
import { AuthContext, useAuthProvider } from "@/hooks/useAuth";
import { LoginPage } from "@/pages/LoginPage";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SetupPage } from "@/pages/SetupPage";
import { StartingUpPage } from "@/pages/StartingUpPage";
import { SeekHarnessPage } from "@/pages/SeekHarnessPage";
import { VisualCCHarnessPage } from "@/pages/VisualCCHarnessPage";
import { ClientUpdateBanner } from "@/components/ClientUpdateBanner";
import { useClientBuildFreshness } from "@/hooks/useClientBuildFreshness";
import type { ReactNode } from "react";

export function App() {
  if (typeof window !== "undefined" && window.location.pathname === "/__debug/seek-harness") {
    return (
      <ErrorBoundary>
        <SeekHarnessPage />
      </ErrorBoundary>
    );
  }
  if (typeof window !== "undefined" && window.location.pathname === "/__debug/visual-cc-harness") {
    return (
      <ErrorBoundary>
        <VisualCCHarnessPage />
      </ErrorBoundary>
    );
  }

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const auth = useAuthProvider();
  const buildFreshness = useClientBuildFreshness();

  let content: ReactNode;
  if (auth.loading) {
    content = <div className="app-loading">Loading...</div>;
  } else if (auth.apiStartingUp) {
    content = (
      <AuthContext.Provider value={auth}>
        <ErrorBoundary>
          <StartingUpPage />
        </ErrorBoundary>
      </AuthContext.Provider>
    );
  } else if (!auth.authenticated) {
    content = (
      <AuthContext.Provider value={auth}>
        <ErrorBoundary>
          {auth.setupRequired ? <SetupPage /> : <LoginPage />}
        </ErrorBoundary>
      </AuthContext.Provider>
    );
  } else {
    content = (
      <AuthContext.Provider value={auth}>
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </AuthContext.Provider>
    );
  }

  return (
    <>
      <ClientUpdateBanner visible={buildFreshness.stale} />
      {content}
    </>
  );
}
